import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, MobileSlideDialog, DialogActions, DialogContent, DialogTitle, IconButton, CloseIcon } from '@/ui';
import DialogScreen from '../../engine/renderer/DialogScreen';
import referedMethod from '../../engine/schema/onboarding/2-refered-method.json';
import referedFromCompany from '../../engine/schema/onboarding/3-refered-from-company.json';
import { useUI } from '@/shared/state/store';
import { OnboardingService } from '@/shared/api/services/onboardingService';
import { LenderService } from '@/shared/api/services/lenderService';
import { DebtsService } from '@/shared/api/services/debtsService';
import type { LenderParentRecord, AccountTypeRecord } from '@/shared/api/services/lenderService';
import type { CustomerSharingUpsertRequest } from '@/shared/api/types';
import { BARCLAYS_MM_LENDER_NAME, isPrearrearsCustomer } from '@/shared/prearrears/ctToken';

type ScreenSchema = Parameters<typeof DialogScreen>[0]['schema'];

interface CompanyReferralEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  userId: number;
  userEmail?: string;
  prefillStep0?: Record<string, unknown>;
  prefillStep1?: Record<string, unknown>;
}

const OTHER_COMPANY_OPTION = {
  value: 'OTHER_COMPANY',
  label: 'Can\'t Find my referring Company',
};

// Ensure DialogScreen/RHF never receives undefined initial values
// (prevents uncontrolled -> controlled input warning when prefill arrives async).
const DEFAULT_STEP0_DATA: Record<string, unknown> = {
  referedFrom: '',
};
const DEFAULT_STEP1_DATA: Record<string, unknown> = {
  referedFromCompany: '',
  otherCompanyName: '',
  productName: '',
  accountType: '',
  referenceId: '',
  noReferenceId: false,
  notReferredByCompany: false,
};

const normalize = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const normalizeLower = (v: unknown): string => normalize(v).toLowerCase();
const isAffirmative = (v: unknown): boolean => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === 'y' || s === 'yes' || s === 'true' || s === '1';
  }
  return false;
};
const toYN = (value: unknown, fallback: 'Y' | 'N' = 'N'): 'Y' | 'N' => {
  if (value === 'Y') return 'Y';
  if (value === 'N') return 'N';
  return fallback;
};
// When a referred/reference company is added to the Share List from referral flows,
// we default it to selected (shareNow = "Y").
const shouldDefaultShareNow = (_lenderName: string): boolean => true;

// Keep dialog behaviour consistent with onboarding screen:
// Hide specific company names universally (Barclays MM is conditional unless pre-arrears)
const HIDDEN_COMPANY_NAMES = [
  'Yorkshire Water Community Trust',
  'Vulnerability Registration Service',
  'PayPlan',
  'National Debtline',
];
const hiddenCompanyNameSet = new Set(HIDDEN_COMPANY_NAMES.map((name) => name.trim().toLowerCase()));
const BARCLAYS_MM_KEY = BARCLAYS_MM_LENDER_NAME.trim().toLowerCase();
const COMPANY_REFERRAL_METHODS = new Set([
  'Referred by a company',
  'Debt charity or advice service'
]);

const CompanyReferralEditDialog: React.FC<CompanyReferralEditDialogProps> = ({
  open,
  onClose,
  onSaved,
  userId,
  userEmail,
  prefillStep0,
  prefillStep1,
}) => {
  const { setLoading, addAlert } = useUI();
  const handleDialogClose = useCallback(() => {
    // Avoid leaving focus inside a dialog that is being aria-hidden during close animations
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
    onClose();
  }, [onClose]);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isPrearrears, setIsPrearrears] = useState<boolean>(() => isPrearrearsCustomer(null));
  const [step0Data, setStep0Data] = useState<Record<string, unknown>>(() => ({
    ...DEFAULT_STEP0_DATA,
    ...(prefillStep0 ?? {}),
  }));
  const [step1Data, setStep1Data] = useState<Record<string, unknown>>(() => ({
    ...DEFAULT_STEP1_DATA,
    ...(prefillStep1 ?? {}),
  }));
  const [isCreditorReferral, setIsCreditorReferral] = useState<boolean>(false);

  const step0FormId = 'company-referral-step0';
  const step1FormId = 'company-referral-step1';

  // lenders and dynamic options
  const [lenders, setLenders] = useState<LenderParentRecord[]>([]);
  const [lendersLoaded, setLendersLoaded] = useState<boolean>(false);
  const [accountTypes, setAccountTypes] = useState<AccountTypeRecord[]>([]);
  const accountTypesKeyRef = useRef<string | null>(null);
  const [formInstanceKey, setFormInstanceKey] = useState<number>(0);
  const autoSelectedAccountTypeRef = useRef<string | null>(null);
  const accountTypesLengthRef = useRef<number>(0);
  const hasPrefilledCreditorRef = useRef<boolean>(false);

  const selectedReferMethod = useMemo(() => normalize(step0Data?.['referedFrom']), [step0Data]);
  const requiresCompanySelection = useMemo(() => (
    selectedReferMethod === 'Referred by a company' || selectedReferMethod === 'Debt charity or advice service'
  ), [selectedReferMethod]);

  const companyValue = useMemo(() => normalize(step1Data?.['referedFromCompany']), [step1Data]);
  const notReferredByCompany = useMemo(() => Boolean(step1Data?.['notReferredByCompany'] === true), [step1Data]);
  const productValue = useMemo(() => normalize(step1Data?.['productName']), [step1Data]);
  const accountTypeValue = useMemo(() => normalize(step1Data?.['accountType']), [step1Data]);

  const accountTypeLookupKey = useMemo(() => {
    const trimmedCompany = (companyValue || '').trim();
    const trimmedProduct = (productValue || '').trim();
    const isOtherCompany = companyValue === OTHER_COMPANY_OPTION.value;
    return isOtherCompany ? OTHER_COMPANY_OPTION.value : (trimmedProduct || trimmedCompany);
  }, [companyValue, productValue]);

  // IMPORTANT: accountTypesKeyRef is a ref (doesn't trigger renders). Compute "loaded for key"
  // directly each render so we don't get stuck in a "loading" state when only the ref changes.
  const accountTypesLoadedForKey =
    accountTypesKeyRef.current === accountTypeLookupKey;
  const singleAccountTypeCodeForKey = (() => {
    if (!accountTypesLoadedForKey) return '';
    const unique = Array.from(
      new Set(
        accountTypes
          .map((t) => (t?.code ?? '').toString().trim())
          .filter((c) => c.length > 0)
      )
    );
    return unique.length === 1 ? unique[0] : '';
  })();

  // Must be declared BEFORE any hook that references it (deps array / callbacks).
  // Otherwise React will evaluate `resolveIds` while it's still in the TDZ and crash.
  const resolveIds = useCallback(async (): Promise<{ recordId: number | null; customerId: number | null }> => {
    try {
      const ls = localStorage.getItem('customerData');
      if (ls) {
        const obj = JSON.parse(ls) as Record<string, unknown>;
        const rid =
          typeof obj['id'] === 'number'
            ? obj['id']
            : typeof obj['customerId'] === 'number'
              ? obj['customerId']
              : null;
        const cid =
          typeof obj['customerId'] === 'number'
            ? obj['customerId']
            : typeof obj['id'] === 'number'
              ? obj['id']
              : null;
        if (rid && cid) return { recordId: rid, customerId: cid };
      }
    } catch {}

    try {
      const resp = await OnboardingService.getCustomerByUserId(userId, { skipErrorCapture: true });
      const data = resp?.data as Record<string, unknown> | undefined;
      if (data) {
        const rid =
          typeof data['id'] === 'number'
            ? data['id']
            : typeof data['customerId'] === 'number'
              ? data['customerId']
              : null;
        const cid =
          typeof data['customerId'] === 'number'
            ? data['customerId']
            : typeof data['id'] === 'number'
              ? data['id']
              : null;
        return { recordId: rid, customerId: cid };
      }
    } catch {}

    return { recordId: null, customerId: null };
  }, [userId]);

  // Load lenders list
  useEffect(() => {
    if (!open || lendersLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading('company-lenders-load', true);
        const resp = await LenderService.fetchLenders({ skipErrorCapture: true });
        if (!cancelled) {
          if (resp.success && Array.isArray(resp.data)) {
            setLenders(resp.data);
          }
          setLendersLoaded(true);
        }
      } catch {
        if (!cancelled) setLendersLoaded(true);
      } finally {
        if (!cancelled) setLoading('company-lenders-load', false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, lendersLoaded, setLoading]);

  // Load customer textField2 to determine Creditor behavior and prefill forms
  useEffect(() => {
    if (!open) return;
    // Only run once per dialog open
    if (hasPrefilledCreditorRef.current) return;
    
    let cancelled = false;
    (async () => {
      try {
        const resp = await OnboardingService.getCustomerByUserId(userId, { skipErrorCapture: true });
        if (cancelled) return;
        let customer: Record<string, unknown> | null = null;
        const data = resp?.data as unknown;
        if (Array.isArray(data) && data.length > 0) customer = data[0] as Record<string, unknown>;
        else if (data && typeof data === 'object') customer = data as Record<string, unknown>;
        
        if (!customer) {
          // Fallback to stored token-based detection (if present)
          setIsPrearrears(isPrearrearsCustomer(null));
          setIsCreditorReferral(false);
          return;
        }

        // Barclays pre-arrears detection (customerType from backend or stored token fallback)
        setIsPrearrears(isPrearrearsCustomer(customer));
        
        const tf2 = (typeof customer['textField2'] === 'string') ? customer['textField2'].trim() : '';
        const tf3 = (typeof customer['textField3'] === 'string') ? customer['textField3'].trim() : '';
        const f5 = (typeof customer['field5TxtFld'] === 'string') ? customer['field5TxtFld'].trim() : '';
        
        const isCreditor = tf2.toLowerCase() === 'creditor';
        setIsCreditorReferral(isCreditor);
        
        // Check all conditions for creditor-invited customer
        const isCreditorInvited = isCreditor && tf3 && f5 && tf3 === f5;
        
        if (!isCreditorInvited) return;
        
        // Check current state to decide if we should prefill
        const hasStep0Value = step0Data?.referedFrom && typeof step0Data.referedFrom === 'string' && step0Data.referedFrom.trim().length > 0;
        const hasStep1Company = step1Data?.referedFromCompany && typeof step1Data.referedFromCompany === 'string' && step1Data.referedFromCompany.trim().length > 0;
        const isOtherCompany = step1Data?.referedFromCompany === OTHER_COMPANY_OPTION.value;
        
        // Only skip prefill if we have valid data that's NOT "OTHER_COMPANY"
        if (hasStep0Value && hasStep1Company && !isOtherCompany) {
          hasPrefilledCreditorRef.current = true;
          return;
        }
        
        // IMPORTANT: Set step0 immediately, don't wait for lenders
        // This ensures selectedReferMethod is set before step 1 renders
        setStep0Data((prev) => ({ ...prev, referedFrom: 'Referred by a company' }));
        
        // Wait for lenders to load before matching company
        if (!lendersLoaded) {
          // Will run again when lendersLoaded becomes true
          return;
        }
        
        hasPrefilledCreditorRef.current = true;
        
        // Prefill step 1: Match company name against lenders (case-insensitive)
        const companyName = f5;
        
        // First, try matching as parent company
        let matchedLender = lenders.find((l) => 
          typeof l.parentCompany === 'string' && 
          l.parentCompany.trim().toLowerCase() === companyName.toLowerCase()
        );
        
        let matchedProduct: string | null = null;
        
        // If not found as parent, try matching as child company (product)
        if (!matchedLender) {
          for (const lender of lenders) {
            const childCompanies = Array.isArray(lender.childCompany) ? lender.childCompany : [];
            const foundChild = childCompanies.find((child) => 
              typeof child === 'string' && 
              child.trim().toLowerCase() === companyName.toLowerCase()
            );
            
            if (foundChild) {
              matchedLender = lender;
              matchedProduct = foundChild;
              break;
            }
          }
        }
        
        if (matchedLender) {
          const step1Prefill: Record<string, unknown> = {
            referedFromCompany: matchedLender.parentCompany,
          };
          
          // If matched as a child company (product), use that
          if (matchedProduct) {
            step1Prefill.productName = matchedProduct;
          } else {
            // If matched as parent and has single product, prefill it
            const childCompanies = Array.isArray(matchedLender.childCompany) ? matchedLender.childCompany : [];
            if (childCompanies.length === 1) {
              step1Prefill.productName = childCompanies[0];
            }
          }
          
          setStep1Data((prev) => ({ ...prev, ...step1Prefill }));
        }
        
        // Force form re-render with new data
        setFormInstanceKey((k) => k + 1);
      } catch {
        if (!cancelled) setIsCreditorReferral(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, lendersLoaded, lenders, step0Data, step1Data]);

  // Barclays pre-arrears: force referred-company selection and lock down dependent fields.
  useEffect(() => {
    if (!open) return;
    if (!isPrearrears) return;

    setStep0Data((prev) => ({ ...prev, referedFrom: 'Referred by a company' }));
    setStep1Data((prev) => ({
      ...prev,
      referedFromCompany: BARCLAYS_MM_LENDER_NAME,
      otherCompanyName: '',
      productName: '',
      accountType: '',
      referenceId: '',
      noReferenceId: false,
      notReferredByCompany: false,
    }));
    // Ensure the DialogScreen remounts to pick up forced defaults
    setFormInstanceKey((k) => k + 1);
  }, [open, isPrearrears]);

  // Filter lenders based on referral method and hide specific names (parity with onboarding)
  const filteredLenders = useMemo(() => {
    const sanitized = lenders.filter((rec) => {
      const parentName = typeof rec.parentCompany === 'string' ? rec.parentCompany.trim().toLowerCase() : '';
      const lenderName = typeof (rec as { lender?: string }).lender === 'string' ? (rec as { lender?: string }).lender!.trim().toLowerCase() : '';
      const descriptionName = typeof rec.description === 'string' ? rec.description.trim().toLowerCase() : '';
      return (
        !hiddenCompanyNameSet.has(parentName) &&
        !hiddenCompanyNameSet.has(lenderName) &&
        !hiddenCompanyNameSet.has(descriptionName) &&
        // Legacy parity: Barclays Money Management is only visible for pre-arrears users
        (isPrearrears ||
          (parentName !== BARCLAYS_MM_KEY &&
            lenderName !== BARCLAYS_MM_KEY &&
            descriptionName !== BARCLAYS_MM_KEY))
      );
    });
    
    // Skip filtering for creditor referrals - show all companies (except hidden ones)
    // because creditor has already provided the specific company name
    if (isCreditorReferral) {
      return sanitized;
    }
    
    // Show full list for both company and debt-charity referrals; other
    // methods already fall back to the sanitized list.
    return sanitized;
  }, [lenders, selectedReferMethod, isCreditorReferral, isPrearrears]);

  // Compute company options from filtered lenders
  const companyOptions = useMemo(() => {
    if (isPrearrears) {
      return [{ value: BARCLAYS_MM_LENDER_NAME, label: BARCLAYS_MM_LENDER_NAME }];
    }
    const unique = new Map<string, { value: string; label: string }>();
    filteredLenders.forEach((rec) => {
      const label = typeof rec.parentCompany === 'string' ? rec.parentCompany.trim() : '';
      if (label) unique.set(label, { value: label, label });
    });
    const sorted = Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label));
    return [OTHER_COMPANY_OPTION, ...sorted];
  }, [filteredLenders, isPrearrears]);

  // Find selected company record
  const selectedCompany: LenderParentRecord | null = useMemo(() => {
    if (!companyValue || companyValue === OTHER_COMPANY_OPTION.value) return null;
    const rec = filteredLenders.find((l) => normalize(l.parentCompany) === normalize(companyValue));
    return rec || null;
  }, [filteredLenders, companyValue]);

  // If current selection is not present after filtering (based on step 0), clear dependent fields
  useEffect(() => {
    if (!open) return;
    // Do not clear prefilled values before lender options have loaded; otherwise
    // we can wipe valid saved selections (company/account type) during initial mount.
    if (!lendersLoaded) return;
    const hasCompany = !!companyValue && companyValue !== OTHER_COMPANY_OPTION.value;
    if (!hasCompany) return;
    const exists = filteredLenders.some((l) => normalize(l.parentCompany) === normalize(companyValue));
    if (!exists) {
      setStep1Data((prev) => ({
        ...prev,
        referedFromCompany: '',
        productName: '',
        accountType: '',
        referenceId: '',
        noReferenceId: false,
      }));
      setAccountTypes([]);
      accountTypesKeyRef.current = null;
      setFormInstanceKey((k) => k + 1);
    }
  }, [open, lendersLoaded, filteredLenders, companyValue]);

  // Normalize case where a child-company (product) was persisted as company
  useEffect(() => {
    if (!open || !lendersLoaded) return;
    const cv = companyValue;
    if (!cv || cv === OTHER_COMPANY_OPTION.value) return;

    const cvNorm = normalize(cv);
    let parent: LenderParentRecord | null = null;
    let childHit: string | undefined;
    for (const l of lenders) {
      const children = Array.isArray(l.childCompany) ? l.childCompany : [];
      for (const child of children) {
        if (normalize(child) === cvNorm) {
          parent = l;
          childHit = child;
          break;
        }
      }
      if (parent) break;
    }
    if (parent) {
      setStep1Data((prev) => ({
        ...prev,
        // Normalize company to parent but preserve existing selections
        referedFromCompany: typeof parent?.parentCompany === 'string' ? parent.parentCompany.trim() : cv,
        productName: (typeof prev?.productName === 'string' && (prev.productName as string).trim().length > 0)
          ? (prev.productName as string)
          : (productValue || childHit || ''),
      }));
      // Refresh options; do not wipe saved values
      setAccountTypes([]);
      accountTypesKeyRef.current = null;
      setFormInstanceKey((k) => k + 1);
    }
  }, [open, lendersLoaded, lenders, companyValue, productValue]);

  // Ensure product select shows normalized value after options load
  useEffect(() => {
    if (!open || currentStep !== 1) return;
    const children = selectedCompany?.childCompany ?? [];
    const p = productValue;
    if (p && children.length > 0 && !children.includes(p)) {
      // If current product is invalid for newly selected parent, try to map by normalization or clear
      const match = children.find((c) => normalize(c) === normalize(p));
      setStep1Data((prev) => ({ ...prev, productName: match || '' }));
      setFormInstanceKey((k) => k + 1);
    }
  }, [open, currentStep, selectedCompany, productValue]);

  // Load account types when needed
  useEffect(() => {
    if (!open) return;
    const isOtherCompany = companyValue === OTHER_COMPANY_OPTION.value;
    const trimmedProduct = productValue.trim();

    if (!requiresCompanySelection || notReferredByCompany || !accountTypeLookupKey) {
      // IMPORTANT:
      // Avoid infinite render loops: this effect depends on `accountTypes`, and calling
      // `setAccountTypes([])` repeatedly (even when already empty) creates a new array
      // reference every time -> re-render -> re-run effect -> ...
      setAccountTypes((prev) => (prev.length > 0 ? [] : prev));
      // Reset the "loaded key" so if the user later selects a company (or returns to step-1)
      // we will fetch again instead of incorrectly skipping due to stale refs.
      // IMPORTANT: do NOT clear `step1Data.accountType` here; we only reset option-state.
      accountTypesKeyRef.current = null;
      accountTypesLengthRef.current = 0;
      autoSelectedAccountTypeRef.current = null;
      // Clear stale account type when no options are applicable
      // setStep1Data((prev) => ({ ...prev, accountType: '' }));
      return;
    }

    // Respect addToDebtList rules: for regular digital companies, only fetch/show account types
    // when addToDebtList === 'Y'. OTHER_COMPANY is allowed to load account types.
    try {
      // Parent-level flag
      const parentAddToDebtY =
        isAffirmative(selectedCompany?.addToDebtList) ||
        isAffirmative((selectedCompany?.raw as any)?.addToDebtList);
      // Child override if a product is selected
      let childAddToDebtY: boolean | undefined;
      if (trimmedProduct && selectedCompany && Array.isArray((selectedCompany as any).childrenRaw)) {
        const childrenRaw = (selectedCompany as unknown as { childrenRaw?: Array<Record<string, unknown>> }).childrenRaw ?? [];
        const pNorm = normalize(trimmedProduct);
        const childRawMatch = childrenRaw.find((child) => {
          const childLender = normalize((child as { lender?: string }).lender);
          const childDesc = normalize((child as { description?: string }).description);
          return childLender === pNorm || childDesc === pNorm;
        });
        if (childRawMatch) {
          childAddToDebtY = isAffirmative((childRawMatch as { addToDebtList?: unknown }).addToDebtList);
        }
      }
      const effectiveAddToDebtY = (childAddToDebtY !== undefined) ? childAddToDebtY : parentAddToDebtY;
      if (!isOtherCompany && !effectiveAddToDebtY) {
        // Clear any previously loaded account types if addToDebt is N
        if (accountTypesKeyRef.current !== null || accountTypes.length > 0) {
          setAccountTypes([]);
        }
        // Mark this key as "loaded with zero options" so UI can hide the field
        // and we don't preserve/restore an account type from a previous company.
        accountTypesKeyRef.current = accountTypeLookupKey;
        accountTypesLengthRef.current = 0;
        autoSelectedAccountTypeRef.current = null;
        setStep1Data((prev) => ({ ...prev, accountType: '' }));
        return;
      }
    } catch {
      // If anything goes wrong, fall back to safe behaviour (no fetch)
      if (!isOtherCompany) {
        setAccountTypes((prev) => (prev.length > 0 ? [] : prev));
        accountTypesKeyRef.current = accountTypeLookupKey;
        accountTypesLengthRef.current = 0;
        autoSelectedAccountTypeRef.current = null;
        setStep1Data((prev) => ({ ...prev, accountType: '' }));
        return;
      }
    }

    // Check if we already have account types for this company (using ref to avoid re-fetching)
    if (accountTypesKeyRef.current === accountTypeLookupKey) {
      return;
    }

    // Always fetch account types for the selected company (or all, for OTHER)
    const shouldFetchAllAccountTypes = isOtherCompany;

    let cancelled = false;
    (async () => {
      try {
        setLoading('company-account-types', true);
        const resp = shouldFetchAllAccountTypes
          ? await LenderService.fetchAllAccountTypes({ skipErrorCapture: true })
          : await LenderService.fetchAccountTypesByCompany(accountTypeLookupKey, { skipErrorCapture: true });
        if (!cancelled) {
          const newAccountTypes = Array.isArray(resp.data) ? resp.data : [];
          setAccountTypes(newAccountTypes);
          accountTypesKeyRef.current = accountTypeLookupKey;
          accountTypesLengthRef.current = newAccountTypes.length;
          // Auto-select single option and hide field
          const unique = Array.from(new Set(newAccountTypes.map((t: any) => (t?.code ?? '').toString().trim()).filter((c: string) => c.length > 0)));
          if (unique.length === 1) {
            const auto = unique[0];
            autoSelectedAccountTypeRef.current = auto;
            // Auto-apply the value so debt/update payloads are correct even when we hide the dropdown.
            setStep1Data((prev) => ({ ...prev, accountType: auto }));
          } else {
            autoSelectedAccountTypeRef.current = null;
          }
        }
      } catch {
        if (!cancelled) {
          setAccountTypes((prev) => (prev.length > 0 ? [] : prev));
          accountTypesKeyRef.current = accountTypeLookupKey;
          accountTypesLengthRef.current = 0;
          autoSelectedAccountTypeRef.current = null;
        }
      } finally {
        if (!cancelled) setLoading('company-account-types', false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    open,
    companyValue,
    productValue,
    requiresCompanySelection,
    notReferredByCompany,
    selectedCompany,
    accountTypes,
    setLoading,
  ]);

  // Dynamic schema for step 1
  const step1Schema: ScreenSchema = useMemo(() => {
    const base = referedFromCompany as unknown as ScreenSchema;
    const section = base.sections?.[0];
    if (!section) return base;

    const childCompanies = selectedCompany?.childCompany ?? [];
    const productOptions = childCompanies.map((c) => ({ value: c, label: c }));
    const accountTypeOptions = accountTypes.map((t) => {
      const typed = t as unknown as { code?: unknown; label?: unknown; description?: unknown };
      const codeStr = typeof typed.code === 'string' ? typed.code : String(typed.code ?? '');
      const labelCandidate = typeof typed.label === 'string' && typed.label.trim().length > 0
        ? typed.label
        : (typeof typed.description === 'string' && typed.description.trim().length > 0 ? typed.description : codeStr);
      return { value: codeStr, label: labelCandidate };
    });
    const addToDebtListY = isAffirmative(selectedCompany?.addToDebtList) || isAffirmative((selectedCompany?.raw as any)?.addToDebtList);
    const showCompanyListY = isAffirmative(selectedCompany?.showCompanyList) || isAffirmative((selectedCompany?.raw as any)?.showCompanyList);

    const hasCompany = !!companyValue && companyValue !== OTHER_COMPANY_OPTION.value && !notReferredByCompany;
    const shouldShowProduct = hasCompany && productOptions.length > 0;
    // Align visibility with onboarding rules and handle OTHER_COMPANY
    const isOtherCompany = companyValue === OTHER_COMPANY_OPTION.value;
    const uniqueAccountTypeCodes = Array.from(
      new Set(
        accountTypeOptions
          .map((opt) => String(opt.value).trim())
          .filter((c) => c.length > 0)
      )
    );
    const hasMultipleAccountTypes = uniqueAccountTypeCodes.length > 1;
    // IMPORTANT: the engine clears values for hidden fields. Account types load async,
    // so if we hide the field while loading we can lose the prefilled accTypeCode (e.g. "WA").
    // Keep the field visible whenever it is conceptually applicable; only make it required
    // once we have multiple options.
    const canHaveAccountType =
      !!companyValue &&
      !notReferredByCompany &&
      (isOtherCompany || addToDebtListY);
    // UX rule:
    // - If there is exactly ONE account type, we do NOT show the dropdown; we treat it as selected.
    // Engine rule:
    // - Hidden fields get cleared by the engine, so during async loading we keep the field visible
    //   if we already have a value, to prevent it from being wiped before options arrive.
    const isLoadedForCurrentKey = accountTypesLoadedForKey;
    const hasExistingAccountType = accountTypeValue.trim().length > 0;
    const hasLoadedMultipleOptions = isLoadedForCurrentKey && hasMultipleAccountTypes;
    const isLoadingForKey = !isLoadedForCurrentKey;
    const shouldRenderAccountType =
      canHaveAccountType &&
      (hasLoadedMultipleOptions || (isLoadingForKey && hasExistingAccountType));
    const shouldRequireAccountType = canHaveAccountType && hasLoadedMultipleOptions;
    // Include child-specific flags when productName is selected (parity with onboarding)
    let childShowCompanyListY: boolean | undefined;
    let childAddToDebtListY: boolean | undefined;
    if (productValue && selectedCompany && Array.isArray((selectedCompany as any).childrenRaw)) {
      const childrenRaw = (selectedCompany as unknown as { childrenRaw?: Array<Record<string, unknown>> }).childrenRaw ?? [];
      const pNorm = normalize(productValue);
      const childRawMatch = childrenRaw.find((child) => {
        const childLender = normalize((child as { lender?: string }).lender);
        const childDesc = normalize((child as { description?: string }).description);
        return childLender === pNorm || childDesc === pNorm;
      });
      if (childRawMatch) {
        childShowCompanyListY = isAffirmative((childRawMatch as { showCompanyList?: unknown }).showCompanyList);
        childAddToDebtListY = isAffirmative((childRawMatch as { addToDebtList?: unknown }).addToDebtList);
      }
    }
    const effectiveShowCompanyListY = (childShowCompanyListY !== undefined) ? childShowCompanyListY : showCompanyListY;
    const effectiveAddToDebtListY = (childAddToDebtListY !== undefined) ? childAddToDebtListY : addToDebtListY;
    const shouldShowReference = !notReferredByCompany && ((companyValue === OTHER_COMPANY_OPTION.value) || (effectiveAddToDebtListY && effectiveShowCompanyListY && (!!productValue || !shouldShowProduct)));

    const fields = section.fields?.map((field) => {
      // Base field with readonly flag for creditor referrals
      const baseField = {
        ...field,
        ...(isCreditorReferral || isPrearrears ? { isReadOnly: true } : {}),
      };
      
      if (field.fieldName === 'referedFromCompany') {
        return {
          ...baseField,
          options: companyOptions,
          ...(isPrearrears ? { isReadOnly: true } : {}),
          // Clear dependent fields whenever company changes so hidden/visible fields
          // never retain stale values (product/account type/reference/other company name).
          // This also covers switching to 'Can\'t Find my referring Company'.
          events: {
            onChange: [
              // Match onboarding JSON behaviour: only uncheck "not referred" when the user has
              // actually selected a non-empty company value. Clearing the company (e.g. when
              // ticking "Not Referred By a Company") must NOT force the checkbox off.
              {
                when: { op: 'ne', args: [{ var: '$value' }, ''] },
                actions: [{ type: 'setFieldValue', name: 'notReferredByCompany', value: false }],
              },
              {
                // When selecting OTHER_COMPANY (Can\'t Find my referring Company)
                when: { op: 'eq', args: [{ var: '$value' }, OTHER_COMPANY_OPTION.value] },
                actions: [
                  { type: 'setFieldValue', name: 'otherCompanyName', value: '' },
                  { type: 'setFieldValue', name: 'productName', value: '' },
                  { type: 'setFieldValue', name: 'accountType', value: '' },
                  { type: 'setFieldValue', name: 'referenceId', value: '' },
                  { type: 'setFieldValue', name: 'noReferenceId', value: false },
                ],
              },
              {
                // When selecting any regular parent company
                when: { op: 'ne', args: [{ var: '$value' }, OTHER_COMPANY_OPTION.value] },
                actions: [
                  { type: 'setFieldValue', name: 'otherCompanyName', value: '' },
                  { type: 'setFieldValue', name: 'productName', value: '' },
                  { type: 'setFieldValue', name: 'accountType', value: '' },
                  { type: 'setFieldValue', name: 'referenceId', value: '' },
                  { type: 'setFieldValue', name: 'noReferenceId', value: false },
                ],
              },
            ],
          },
        };
      }
      if (field.fieldName === 'otherCompanyName') {
        if (isPrearrears) {
          return { ...baseField, isHidden: true, isRequired: false };
        }
        const show = companyValue === OTHER_COMPANY_OPTION.value && !notReferredByCompany;
        return { ...baseField, isHidden: !show, isRequired: show };
      }
      if (field.fieldName === 'productName') {
        if (isPrearrears) {
          return { ...baseField, isHidden: true, isRequired: false, options: [] };
        }
        return {
          ...baseField,
          isHidden: !shouldShowProduct,
          isRequired: shouldShowProduct,
          options: productOptions,
          // Whenever product changes, clear dependent fields so visibility transitions
          // (account type and reference number) always start blank.
          events: {
            onChange: [
              {
                actions: [
                  { type: 'setFieldValue', name: 'accountType', value: '' },
                  { type: 'setFieldValue', name: 'referenceId', value: '' },
                  { type: 'setFieldValue', name: 'noReferenceId', value: false },
                ],
              },
            ],
          },
        };
      }
      if (field.fieldName === 'accountType') {
        if (isPrearrears) {
          return {
            ...baseField,
            isHidden: true,
            isRequired: false,
            options: [],
            visibleWhen: undefined,
            visibilityCondition: undefined,
            requiredWhen: undefined,
          };
        }
        return {
          ...baseField,
          isHidden: !shouldRenderAccountType,
          isRequired: shouldRequireAccountType,
          options: shouldRenderAccountType ? accountTypeOptions : [],
          // Force-override any schema visibility/required expressions to ensure dropdown shows when we have options
          visibleWhen: undefined,
          visibilityCondition: undefined,
          requiredWhen: undefined,
        };
      }
      if (field.fieldName === 'referenceId') {
        if (isPrearrears) {
          return {
            ...baseField,
            isHidden: true,
            isRequired: false,
            requiredWhen: null,
            ui: field.ui,
          } as any;
        }
        const rules = field.validationRules ? [...field.validationRules] : [];
        // When referred by Creditor, do not show the "I don't have any Reference ID" checkbox and require the field when visible
        const updatedUi = {
          ...(field.ui || {}),
          ...(isCreditorReferral ? { allowNoValue: false } : {}),
        } as any;
        
        // Build the field update object conditionally to avoid setting requiredWhen to undefined
        const fieldUpdate: any = {
          ...baseField,
          isHidden: !shouldShowReference,
          isRequired: true,
          requiredErrorMessage: 'Please enter your reference number',
          validationRules: rules,
          ui: updatedUi,
        };
        
        // Only set requiredWhen when it has a valid value (not undefined)
        if (shouldShowReference && !isCreditorReferral) {
          fieldUpdate.requiredWhen = { op: 'ne', args: [{ var: 'noReferenceId' }, true] };
        } else if (!shouldShowReference) {
          fieldUpdate.requiredWhen = null;
        }
        
        return fieldUpdate;
      }
      if (field.fieldName === 'notReferredByCompany') {
        if (isPrearrears) {
          return { ...baseField, isHidden: true, isRequired: false };
        }
        // Add events to handle both checking and unchecking the boolean field
        const updatedEvents = {
          onChange: [
            {
              when: { op: 'eq', args: [{ var: '$value' }, true] },
              actions: [
                { type: 'setFieldValue', name: 'referedFromCompany', value: '' },
                { type: 'setFieldValue', name: 'otherCompanyName', value: '' },
                { type: 'setFieldValue', name: 'productName', value: '' },
                { type: 'setFieldValue', name: 'accountType', value: '' },
                { type: 'setFieldValue', name: 'referenceId', value: '' },
              ]
            },
            {
              when: { op: 'eq', args: [{ var: '$value' }, false] },
              actions: [
                // When unchecked, just ensure notReferredByCompany is false (no other actions needed)
              ]
            }
          ]
        };
        return {
          ...baseField,
          events: updatedEvents
        };
      }
      return baseField;
    });

    return {
      ...base,
      sections: [
        { ...section, fields },
      ],
    };
  }, [
    selectedCompany,
    companyOptions,
    accountTypes,
    accountTypesLoadedForKey,
    accountTypeValue,
    accountTypesLengthRef.current,
    companyValue,
    notReferredByCompany,
    productValue,
    isCreditorReferral,
  ]);

  // Ensure productName remains valid: if current product isn't in child list, clear it
  useEffect(() => {
    if (!open || currentStep !== 1) return;
    const childCompanies = selectedCompany?.childCompany ?? [];
    const isValidProduct = productValue && childCompanies.includes(productValue);
    if (productValue && !isValidProduct) {
      setStep1Data((prev) => ({ ...prev, productName: '' }));
      //:IMP: Note: we should not reset selected account type since we need to prefill pre existing value
      // also reset account types when product invalidates
      // setAccountTypes([]);
      // accountTypesKeyRef.current = null;
      // Clear stale account type and auto-selection when product changes to invalid
      // autoSelectedAccountTypeRef.current = null;
      // setStep1Data((prev) => ({ ...prev, accountType: '' }));
      setFormInstanceKey((k) => k + 1);
    }
  }, [open, currentStep, selectedCompany, productValue]);

  const step0Schema: ScreenSchema = useMemo(() => {
    const base = referedMethod as unknown as ScreenSchema;
    const section = base.sections?.[0];
    if (!section) return base;

    const currentValue = normalize(step0Data?.['referedFrom']);

    const fields = section.fields?.map((field) => {
      const nextField = (isCreditorReferral || isPrearrears) ? { ...field, isReadOnly: true } : field;

      // If backend stored a legacy value not present in schema options,
      // inject it so the radio selection renders correctly.
      const fieldName =
        typeof (nextField as any)?.fieldName === 'string'
          ? ((nextField as any).fieldName as string)
          : '';
      if (fieldName !== 'referedFrom' || !currentValue) return nextField;

      const options = Array.isArray((nextField as any)?.options)
        ? (((nextField as any).options as unknown[]) || [])
        : [];
      const has = options.some((o) => normalizeLower((o as any)?.value) === currentValue.toLowerCase());
      if (has) return nextField;

      return {
        ...(nextField as any),
        options: [...options, { value: currentValue, label: currentValue }],
      };
    });

    return {
      ...base,
      sections: [{ ...section, fields }],
    };
  }, [isCreditorReferral, step0Data]);

  const liveValuesRef = useRef<Record<string, unknown>>({});
  const lastReferralMethodRef = useRef<string | undefined>(undefined);
  const hasRestoredRefFromDebtsRef = useRef<boolean>(false);
  const restoredProductForCompanyKeyRef = useRef<string | null>(null);
  const autoNoReferenceAppliedRef = useRef<boolean>(false);
  // Self-heal guard for rare state where "not referred" is true but company is still set
  // (can happen if DialogScreen suppresses field events during an in-flight event batch).
  const notReferredCompanyFixRef = useRef<string | null>(null);
  // Debounce guards for handleValuesChange — must live at component level
  // so they persist across re-renders (not on the function object).
  const queuedStep0UpdateRef = useRef<Record<string, unknown> | null>(null);
  const isStep0FlushScheduledRef = useRef(false);
  const queuedStep1UpdateRef = useRef<Record<string, unknown> | null>(null);
  const isStep1FlushScheduledRef = useRef(false);
  const referenceIdValue =
    typeof (step1Data as any)?.referenceId === 'string'
      ? ((step1Data as any).referenceId as string)
      : undefined;
  const noReferenceChecked = (step1Data as any)?.noReferenceId === true;

  // Restore referenceId from debts if it's missing (e.g., after reload), similar to onboarding
  useEffect(() => {
    if (!open || notReferredByCompany || hasRestoredRefFromDebtsRef.current) return;
    const hasRef = typeof (step1Data as any)?.referenceId === 'string' && ((step1Data as any).referenceId as string).trim().length > 0;
    const noRefChecked = (step1Data as any)?.noReferenceId === true;
    const currentAccountType = normalize(step1Data?.['accountType']);
    const hasAccountType = currentAccountType.trim().length > 0;
    // If both fields are already present (or reference is explicitly "unknown"), nothing to restore.
    if ((hasRef || noRefChecked) && hasAccountType) return;

    const selectReferralDebt = (
      rows: Array<Record<string, unknown>>,
      creditorHint: string
    ): Record<string, unknown> | null => {
      if (!Array.isArray(rows) || rows.length === 0) return null;

      const norm = (v: unknown): string =>
        typeof v === 'string' ? v.trim().toLowerCase() : '';

      // Prefer debts created from the profile "referred company" flow.
      // Backwards compatible with legacy rows that used "customer_profile".
      const preferredSources = new Set(['referred', 'customer_profile']);

      const withSource = rows.filter((row) => {
        const rawSource = (row as { source?: unknown }).source;
        const src = norm(rawSource);
        return preferredSources.has(src);
      });

      // If there are no referral-created debts, do NOT fall back to unrelated debts.
      // This prevents accidentally picking arbitrary account numbers.
      if (withSource.length === 0) {
        return null;
      }

      if (creditorHint) {
        const target = creditorHint.trim().toLowerCase();
        const byCreditor = withSource.find((row) => {
          const cred = norm((row as { creditor?: unknown }).creditor);
          return cred === target;
        });
        if (byCreditor) return byCreditor;
        // If we expected a specific creditor but didn't find a match,
        // do not fall back to an unrelated referral debt. This prevents
        // leaking the old company's reference number after changing company.
        return null;
      }

      // Otherwise choose the most recently updated referral debt
      const toTime = (v: unknown): number => {
        if (typeof v === 'string') {
          const t = Date.parse(v);
          return Number.isFinite(t) ? t : 0;
        }
        return 0;
      };
      return (
        withSource
          .slice()
          .sort((a, b) => {
            const ta = Math.max(
              toTime((a as any).lastUpdateDate),
              toTime((a as any).creationDate)
            );
            const tb = Math.max(
              toTime((b as any).lastUpdateDate),
              toTime((b as any).creationDate)
            );
            return tb - ta;
          })[0] ?? null
      );
    };

    // Compute the effective creditor used when the referral debt was created.
    // Mirrors `transformToDebtFormat` in `onboardingService`.
    const effectiveCreditor = (() => {
      const refer = typeof (step1Data as any)?.referedFromCompany === 'string'
        ? (step1Data as any).referedFromCompany
        : '';
      const otherName = typeof (step1Data as any)?.otherCompanyName === 'string'
        ? (step1Data as any).otherCompanyName
        : '';
      const productName = typeof (step1Data as any)?.productName === 'string'
        ? (step1Data as any).productName
        : '';

      if (refer === OTHER_COMPANY_OPTION.value) {
        return otherName;
      }
      if (productName) {
        return productName;
      }
      return refer;
    })();

    let cancelled = false;
    (async () => {
      try {
        hasRestoredRefFromDebtsRef.current = true;
        const ids = await resolveIds();
        if (!ids.customerId || cancelled) return;
        const debts = await OnboardingService.getDebts(ids.customerId as number, { skipErrorCapture: true });
        if (cancelled) return;
        const rows = Array.isArray(debts) ? (debts as Array<Record<string, unknown>>) : [];
        const debt = selectReferralDebt(rows, effectiveCreditor);
        const accTypeRaw =
          debt ? (debt as Record<string, unknown>)?.['accTypeCode'] : undefined;
        const restoredAccountType =
          typeof accTypeRaw === 'string' ? accTypeRaw.trim() : '';
        const cand =
          debt &&
          (typeof (debt as any)?.accountNumber === 'string'
            ? (debt as any).accountNumber
            : typeof (debt as any)?.referenceNumber === 'string'
              ? (debt as any).referenceNumber
              : '');
        try {
          // eslint-disable-next-line no-console
          console.debug('[ReferralDialog:RestoreRef]', {
            debtsCount: Array.isArray(rows) ? rows.length : 0,
            effectiveCreditor,
            selectedDebt: debt
              ? {
                  id: (debt as any)?.id,
                  source: (debt as any)?.source,
                  creditor: (debt as any)?.creditor,
                  accTypeCode: (debt as any)?.accTypeCode,
                  accountNumber: (debt as any)?.accountNumber,
                }
              : null,
            restoredReference: cand,
            restoredAccountType,
          });
        } catch {}
        let didUpdate = false;
        if (!noRefChecked && !hasRef && typeof cand === 'string' && cand.trim().length > 0) {
          didUpdate = true;
          setStep1Data((prev) => ({ ...prev, referenceId: cand }));
        }
        // Also restore accountType (accTypeCode) when missing so the edit dialog
        // pre-selects the correct value from debts (e.g. "WA").
        if (!hasAccountType && restoredAccountType) {
          didUpdate = true;
          setStep1Data((prev) => ({ ...prev, accountType: restoredAccountType }));
        }
        // If step-1 form is currently mounted, remount so DialogScreen receives restored defaults.
        if (didUpdate && currentStep === 1) {
          setFormInstanceKey((k) => k + 1);
        }
      } catch {
        // non-blocking
      }
    })();
    return () => { cancelled = true; };
  }, [open, notReferredByCompany, step1Data, currentStep, resolveIds]);

  useEffect(() => {
    if (!open) return;
    const emailValue = (userEmail ?? '').trim();
    if (!emailValue) return;
    const refValue = (referenceIdValue ?? '').trim();
    if (!refValue) return;
    if (
      refValue.toLowerCase() === emailValue.toLowerCase() &&
      !noReferenceChecked &&
      !autoNoReferenceAppliedRef.current
    ) {
      autoNoReferenceAppliedRef.current = true;
      setStep1Data((prev) => ({
        ...prev,
        noReferenceId: true,
      }));
      setFormInstanceKey((k) => k + 1);
    }
  }, [open, referenceIdValue, noReferenceChecked, userEmail]);

  // Restore missing productName from server prefill if company is selected but product is empty
  useEffect(() => {
    if (!open) return;
    const selectedCompanyKey = companyValue;
    const hasCompany = !!selectedCompanyKey && !notReferredByCompany && selectedCompanyKey !== OTHER_COMPANY_OPTION.value;
    const hasProduct = !!productValue;
    if (!hasCompany || hasProduct) return;

    // Guard: run at most once per company selection to avoid repeated prefill/vulnerability calls
    if (restoredProductForCompanyKeyRef.current === selectedCompanyKey) return;
    restoredProductForCompanyKeyRef.current = selectedCompanyKey;

    let cancelled = false;
    (async () => {
      try {
        const resp = await OnboardingService.loadCustomerPrefillData(userId, { skipErrorCapture: true });
        if (cancelled) return;
        const serverStep1 = resp?.data?.['onboarding-step-1'] as Record<string, unknown> | undefined;
        if (!serverStep1) return;
        const serverCompany = typeof (serverStep1 as any)?.referedFromCompany === 'string' ? (serverStep1 as any).referedFromCompany.trim() : '';
        const serverProduct = typeof (serverStep1 as any)?.productName === 'string' ? (serverStep1 as any).productName.trim() : '';

        // Case 1: Server has product for the same parent company
        if (serverProduct && normalize(serverCompany) === normalize(selectedCompanyKey)) {
          setStep1Data((prev) => ({ ...prev, productName: serverProduct }));
          setFormInstanceKey((k) => k + 1);
          return;
        }

        // Case 2: Server saved child as company; map to product under current parent
        if (!serverProduct && serverCompany) {
          const parentRec = lenders.find((l) => normalize(l.parentCompany) === normalize(selectedCompanyKey));
          const childMatch = parentRec && Array.isArray(parentRec.childCompany)
            ? parentRec.childCompany.find((c) => normalize(c) === normalize(serverCompany))
            : undefined;
          if (childMatch) {
            setStep1Data((prev) => ({ ...prev, productName: childMatch }));
            setFormInstanceKey((k) => k + 1);
          }
        }
      } catch {
        // non-blocking
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, lenders, companyValue, productValue, notReferredByCompany]);

  const handleValuesChange = (vals: unknown) => {
    const rec = vals as Record<string, unknown>;
    liveValuesRef.current = rec;

    const enqueueStep0Update = (partial: Record<string, unknown>) => {
      queuedStep0UpdateRef.current = { ...(queuedStep0UpdateRef.current || {}), ...partial };
      if (!isStep0FlushScheduledRef.current) {
        isStep0FlushScheduledRef.current = true;
        setTimeout(() => {
          const update = queuedStep0UpdateRef.current;
          queuedStep0UpdateRef.current = null;
          isStep0FlushScheduledRef.current = false;
          if (update) setStep0Data((prev) => ({ ...prev, ...update }));
        }, 0);
      }
    };
    const enqueueStep1Update = (partial: Record<string, unknown>) => {
      queuedStep1UpdateRef.current = { ...(queuedStep1UpdateRef.current || {}), ...partial };
      if (!isStep1FlushScheduledRef.current) {
        isStep1FlushScheduledRef.current = true;
        setTimeout(() => {
          const update = queuedStep1UpdateRef.current;
          queuedStep1UpdateRef.current = null;
          isStep1FlushScheduledRef.current = false;
          if (update) setStep1Data((prev) => ({ ...prev, ...update }));
        }, 0);
      }
    };
    if (currentStep === 0) {
      const incomingMethod = typeof rec.referedFrom === 'string' ? rec.referedFrom.trim() : '';
      const prevMethod = (lastReferralMethodRef.current ?? (typeof (step0Data as any)?.referedFrom === 'string' ? (step0Data as any).referedFrom.trim() : ''));
      const hasMeaningfulSelection = incomingMethod.length > 0;
      const changed = hasMeaningfulSelection && incomingMethod !== prevMethod;
      
      // IMPORTANT: DialogScreen can call onValuesChange on mount with empty values.
      // Do NOT let that overwrite a valid prefilled selection (radio would appear "unset").
      if (!hasMeaningfulSelection && prevMethod) {
        // Keep refs stable, but ignore the empty payload.
        return;
      }

      // Update step0Data to keep radio selection in sync with user input
      enqueueStep0Update({ ...rec });
      
      if (changed && !COMPANY_REFERRAL_METHODS.has(incomingMethod)) {
        // Clear step-1 data and dependent dynamic state when referral method changes
        const clearedStep1State: Record<string, unknown> = {
          referedFromCompany: '',
          otherCompanyName: '',
          productName: '',
          accountType: '',
          referenceId: '',
          noReferenceId: false,
          notReferredByCompany: false,
        };
        enqueueStep1Update(clearedStep1State);
        setAccountTypes([]);
        accountTypesKeyRef.current = null;
        autoSelectedAccountTypeRef.current = null;
        restoredProductForCompanyKeyRef.current = null;
        hasRestoredRefFromDebtsRef.current = false;
        setFormInstanceKey((k) => k + 1);
      }
      lastReferralMethodRef.current = hasMeaningfulSelection ? incomingMethod : lastReferralMethodRef.current;
    }
    if (currentStep === 1) {
      // If the current company has exactly one account type (and we hide the dropdown),
      // the engine will clear hidden field values and send `accountType: undefined` here.
      // Preserve the implied/auto-selected value so saves/debt updates remain correct.
      const normalizedRec: Record<string, unknown> = { ...rec };
      const incomingAccountType = normalize(normalizedRec.accountType);
      if (
        singleAccountTypeCodeForKey &&
        accountTypesLoadedForKey &&
        incomingAccountType.length === 0
      ) {
        normalizedRec.accountType = singleAccountTypeCodeForKey;
      }

      // Self-heal rare inconsistent state: checkbox is true (select disabled),
      // but company still has a value. In that state, saving can accidentally
      // persist the company unless we force-clear.
      const incomingCompanyForFix = typeof normalizedRec.referedFromCompany === 'string' ? normalizedRec.referedFromCompany.trim() : '';
      const incomingNotReferredForFix = normalizedRec.notReferredByCompany === true;
      if (incomingNotReferredForFix && incomingCompanyForFix) {
        if (notReferredCompanyFixRef.current !== incomingCompanyForFix) {
          notReferredCompanyFixRef.current = incomingCompanyForFix;
          const clearedStep1State: Record<string, unknown> = {
            ...normalizedRec,
            referedFromCompany: '',
            otherCompanyName: '',
            productName: '',
            accountType: '',
            referenceId: '',
            noReferenceId: false,
          };
          enqueueStep1Update(clearedStep1State);
          setAccountTypes([]);
          accountTypesKeyRef.current = null;
          autoSelectedAccountTypeRef.current = null;
          restoredProductForCompanyKeyRef.current = null;
          hasRestoredRefFromDebtsRef.current = false;
          // Remount the form AFTER the queued step1Data update flushes, so initialData
          // used by DialogScreen reflects the cleared values.
          setTimeout(() => setFormInstanceKey((k) => k + 1), 0);
          return;
        }
      } else if (!incomingNotReferredForFix) {
        notReferredCompanyFixRef.current = null;
      }

      // If company changes, clear dependent fields to avoid leaking product into company
      const incomingCompany = typeof normalizedRec.referedFromCompany === 'string' ? normalizedRec.referedFromCompany.trim() : '';
      const prevCompany = typeof step1Data?.['referedFromCompany'] === 'string' ? (step1Data['referedFromCompany'] as string).trim() : '';

      // Handle notReferredByCompany changes - only update if value actually changed to prevent infinite loop
      const prevNotReferred = step1Data?.notReferredByCompany;
      const newNotReferred = normalizedRec.notReferredByCompany;
      if (newNotReferred !== undefined && newNotReferred !== prevNotReferred) {
        enqueueStep1Update({ ...normalizedRec });
        // Clear account types since they depend on company selection.
        // Do this for both checking and unchecking to avoid stale dropdown state.
        setAccountTypes([]);
        accountTypesKeyRef.current = null;
        // Don't call setFormInstanceKey here - it causes infinite loop
        return;
      }

      if (incomingCompany && incomingCompany !== prevCompany) {
        // Preserve product if it is a valid child of the new parent company
        const nextParent = lenders.find((l) => normalize(l.parentCompany) === normalize(incomingCompany));
        const nextChildren = Array.isArray(nextParent?.childCompany) ? nextParent!.childCompany : [];
        const candidateProduct = typeof normalizedRec.productName === 'string' && normalizedRec.productName.trim().length > 0
          ? normalizedRec.productName.trim()
          : (typeof step1Data?.['productName'] === 'string' ? (step1Data['productName'] as string).trim() : '');
        const shouldKeepProduct = candidateProduct && nextChildren.includes(candidateProduct);

        enqueueStep1Update({
          ...normalizedRec,
          notReferredByCompany: false,
          referedFromCompany: incomingCompany,
          productName: shouldKeepProduct ? candidateProduct : '',
          accountType: '',
          referenceId: '',
          noReferenceId: false,
        });
        setAccountTypes([]);
        accountTypesKeyRef.current = null;
        // Don't call setFormInstanceKey here - the useEffect will handle account types loading
        return;
      }

      // keep step1 state in sync for other field changes
      enqueueStep1Update({ ...normalizedRec });
    }
  };

  const handleSubmitStep = async (data: Record<string, unknown>) => {
      if (currentStep === 0) {
      const merged0 = { ...step0Data, ...data };
      setStep0Data(merged0);
      const referMethod = normalize(merged0['referedFrom']);
      const leadsToCompany = referMethod === 'Referred by a company' || referMethod === 'Debt charity or advice service';
      if (!leadsToCompany) {
        // Save immediately, clearing step 1 BUT without deleting existing debts.
        // Instead, we only unlink any profile-managed debt (source === 'customer_profile')
        // so manually-entered debts remain intact.
        try {
          setIsSaving(true);
          setLoading('company-save', true);
          const ids = await resolveIds();
          const payload = {
            'onboarding-step-0': merged0,
            'onboarding-step-1': {},
          } as Record<string, unknown>;
          await OnboardingService.updateCustomer(
            payload,
            userId,
            (ids.recordId as number) || (ids.customerId as number),
            (ids.customerId as number) || (ids.recordId as number),
            (userEmail as string) || '',
            { skipErrorCapture: false }
          );
          handleDialogClose();
          addAlert({ severity: 'success', message: 'Referred method updated', autoHide: true, duration: 1500 });
          // Refresh profile immediately after primary save so the UI updates fast.
          // Optional follow-up calls (unlink debt/sharing) run in the background.
          await onSaved();
          // Background: unlink profile-managed debt(s) without blocking the UI.
          if (ids.customerId) {
            OnboardingService.unlinkProfileManagedDebtAndSharing(
              ids.customerId as number,
              { skipErrorCapture: true }
            ).catch(() => { /* non-blocking */ });
          }
        } catch {
          addAlert({ severity: 'error', message: 'Failed to save referral', autoHide: true, duration: 2000 });
        } finally {
          setIsSaving(false);
          setLoading('company-save', false);
        }
        return;
      }
      setCurrentStep(1);
      return;
    }

    if (currentStep === 1) {
      const merged1Raw = { ...step1Data, ...data } as Record<string, unknown>;
      const notReferred = merged1Raw?.notReferredByCompany === true;

      // Safety net: if the user selected "Not Referred By a Company", ensure we NEVER
      // persist a company/product/reference (and never create debt/share records),
      // even if the UI got into a rare inconsistent state.
      if (notReferred) {
        const cleared: Record<string, unknown> = {
          ...merged1Raw,
          referedFromCompany: '',
          otherCompanyName: '',
          productName: '',
          accountType: '',
          referenceId: '',
          noReferenceId: false,
        };
        setStep1Data(cleared);
        try {
          setIsSaving(true);
          setLoading('company-save', true);
          const ids = await resolveIds();
          const payload = {
            'onboarding-step-0': step0Data,
            'onboarding-step-1': cleared,
          } as Record<string, unknown>;

          await OnboardingService.updateCustomer(
            payload,
            userId,
            (ids.recordId as number) || (ids.customerId as number),
            (ids.customerId as number) || (ids.recordId as number),
            (userEmail as string) || '',
            { skipErrorCapture: false }
          );
          handleDialogClose();
          addAlert({ severity: 'success', message: 'Referred company updated', autoHide: true, duration: 1500 });
          await onSaved();
          // Background: unlink profile-managed debt/sharing without blocking the UI.
          if (ids.customerId) {
            OnboardingService.unlinkProfileManagedDebtAndSharing(
              ids.customerId as number,
              { skipErrorCapture: true }
            ).catch(() => { /* non-blocking */ });
          }
        } catch {
          addAlert({ severity: 'error', message: 'Failed to save referred company', autoHide: true, duration: 2000 });
        } finally {
          setIsSaving(false);
          setLoading('company-save', false);
        }
        return;
      }

      // Normalize/sanitize according to rules used in onboarding flow
      const companyKey = typeof merged1Raw.referedFromCompany === 'string' ? merged1Raw.referedFromCompany : '';
      // Use freshest company from the submitted payload (not memoized selectedCompany, which may be stale)
      const companyRec = lenders.find((l) => normalize(l.parentCompany) === normalize(companyKey));
      const childCompanies = Array.isArray(companyRec?.childCompany) ? companyRec!.childCompany : [];
      const productInOptions = typeof merged1Raw.productName === 'string' && childCompanies.includes(merged1Raw.productName as string);
      const merged1: Record<string, unknown> = {
        ...merged1Raw,
        // Clear productName when not a valid child of the selected parent
        productName: productInOptions ? merged1Raw.productName : '',
      };
      // When OTHER_COMPANY, require otherCompanyName; otherwise clear it
      if (companyKey === OTHER_COMPANY_OPTION.value) {
        // keep otherCompanyName as-is
      } else {
        merged1.otherCompanyName = '';
      }
      // If accountType field is hidden or has only one auto-selected option, keep its value (may be empty)
      // If accountType options were cleared (non-applicable), ensure it's empty
      const accountTypeCodes = accountTypes
        .map((t) => (t?.code ?? '').toString().trim())
        .filter((c) => c.length > 0);
      if (accountTypeCodes.length === 0) {
        // Account types load async.
        // - If we have NOT finished loading for the current company/product key, preserve any
        //   prefilled value (e.g. from debts.accTypeCode) so it can be displayed later.
        // - If we HAVE finished loading for the current key and got zero options, we must
        //   clear accountType to avoid leaking a previous company's code into this save/debt update.
        const selectedCompanyKey = normalize(companyKey);
        const selectedProductKey =
          typeof merged1.productName === 'string' ? merged1.productName.trim() : '';
        const lookupKey =
          companyKey === OTHER_COMPANY_OPTION.value
            ? OTHER_COMPANY_OPTION.value
            : (selectedProductKey || selectedCompanyKey);
        const hasLoadedForKey = accountTypesKeyRef.current === lookupKey;

        const existing =
          typeof (merged1 as any)?.accountType === 'string'
            ? ((merged1 as any).accountType as string).trim()
            : '';
        merged1.accountType = hasLoadedForKey ? '' : existing;
      } else if (accountTypeCodes.length === 1 && autoSelectedAccountTypeRef.current) {
        // Only auto-apply account type when allowed by addToDebtList rules or OTHER_COMPANY
        const parentAddToDebtY =
          isAffirmative(companyRec?.addToDebtList) ||
          isAffirmative((companyRec?.raw as any)?.addToDebtList);
        // Child override if a product is selected
        let childAddToDebtY: boolean | undefined;
        if (
          typeof merged1.productName === 'string' &&
          merged1.productName.trim().length > 0 &&
          companyRec &&
          Array.isArray((companyRec as any).childrenRaw)
        ) {
          const childrenRaw =
            (companyRec as unknown as { childrenRaw?: Array<Record<string, unknown>> })
              .childrenRaw ?? [];
          const pNorm = normalize((merged1.productName as string).trim());
          const childRawMatch = childrenRaw.find((child) => {
            const childLender = normalize((child as { lender?: string }).lender);
            const childDesc = normalize((child as { description?: string }).description);
            return childLender === pNorm || childDesc === pNorm;
          });
          if (childRawMatch) {
            childAddToDebtY = isAffirmative((childRawMatch as { addToDebtList?: unknown }).addToDebtList);
          }
        }
        const effectiveAddToDebtY = (childAddToDebtY !== undefined) ? childAddToDebtY : parentAddToDebtY;
        const isOther = companyKey === OTHER_COMPANY_OPTION.value;
        if (isOther || effectiveAddToDebtY) {
          merged1.accountType = autoSelectedAccountTypeRef.current;
        } else {
          // Explicitly avoid setting account type when addToDebtList is N
          merged1.accountType = '';
        }
      }
      // Persist latest local state before sending to backend so payload reflects the UI
      setStep1Data(merged1);
      try {
        setIsSaving(true);
        setLoading('company-save', true);
        const ids = await resolveIds();
        const payload = {
          'onboarding-step-0': step0Data,
          'onboarding-step-1': merged1,
        } as Record<string, unknown>;

        // Determine effective addToDebtList and addToShareListFromProfile flags
        // by checking the selected product (if any) or falling back to the parent company
        // These flags are INDEPENDENT - one doesn't imply the other
        const isOtherCompany = companyKey === OTHER_COMPANY_OPTION.value;
        const selectedProductName = typeof merged1.productName === 'string' ? merged1.productName.trim() : '';

        // Parent-level flags
        const parentAddToDebtListY =
          isAffirmative(companyRec?.addToDebtList) ||
          isAffirmative((companyRec?.raw as any)?.addToDebtList);
        const parentAddToShareListY =
          isAffirmative(companyRec?.addToShareListFromProfile) ||
          isAffirmative((companyRec?.raw as any)?.addToShareListFromProfile);

        // Child-level flags (if a product is selected)
        let childAddToDebtListY: boolean | undefined;
        let childAddToShareListY: boolean | undefined;
        if (selectedProductName && companyRec && Array.isArray((companyRec as any).childrenRaw)) {
          const childrenRaw = (companyRec as unknown as { childrenRaw?: Array<Record<string, unknown>> }).childrenRaw ?? [];
          const pNorm = normalize(selectedProductName);
          const childRawMatch = childrenRaw.find((child) => {
            const childLender = normalize((child as { lender?: string }).lender);
            const childDesc = normalize((child as { description?: string }).description);
            return childLender === pNorm || childDesc === pNorm;
          });
          if (childRawMatch) {
            childAddToDebtListY = isAffirmative((childRawMatch as { addToDebtList?: unknown }).addToDebtList);
            childAddToShareListY = isAffirmative((childRawMatch as { addToShareListFromProfile?: unknown }).addToShareListFromProfile);
          }
        }

        // Effective flags: prefer child-level if defined, otherwise use parent-level
        // These are INDEPENDENT checks - one doesn't depend on the other
        const effectiveAddToDebtListY = (childAddToDebtListY !== undefined) ? childAddToDebtListY : parentAddToDebtListY;
        const effectiveAddToShareListY = (childAddToShareListY !== undefined) ? childAddToShareListY : parentAddToShareListY;

        // For OTHER_COMPANY, always create debt (legacy behavior for manual entries)
        const shouldCreateDebt = isOtherCompany || effectiveAddToDebtListY;
        // Share list is independent - only check the addToShareListFromProfile flag
        const shouldUpsertSharing = effectiveAddToShareListY;

        // Always update customer data first
        await OnboardingService.updateCustomer(
          payload,
          userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (userEmail as string) || '',
          { skipErrorCapture: false }
        );
        handleDialogClose();

        // Debt-related calls must finish BEFORE refreshProfile so the card shows
        // account type and reference number from the debt record.
        const notReferredFinal = merged1?.notReferredByCompany === true;

        if (notReferredFinal && ids.customerId) {
          try {
            await OnboardingService.unlinkProfileManagedDebtAndSharing(
              ids.customerId as number,
              { skipErrorCapture: true }
            );
          } catch { /* non-blocking */ }
        }

        if (!notReferredFinal && !shouldCreateDebt && ids.customerId) {
          try {
            await OnboardingService.unlinkProfileManagedDebtOnly(
              ids.customerId as number,
              { skipErrorCapture: true }
            );
          } catch { /* non-blocking */ }
        }

        if (shouldCreateDebt && ids.customerId) {
          try {
            await OnboardingService.updateCustomerAndDebt(
              payload,
              userId,
              (ids.recordId as number) || (ids.customerId as number),
              (ids.customerId as number) || (ids.recordId as number),
              (userEmail as string) || '',
              { 
                skipErrorCapture: false,
                skipSharingUpsert: shouldUpsertSharing 
              }
            );
          } catch { /* non-blocking */ }
        }

        addAlert({ severity: 'success', message: 'Referred company updated', autoHide: true, duration: 1500 });
        // Refresh profile after customer + debt records are committed so the
        // card displays account type and reference number correctly.
        await onSaved();

        // Sharing calls can run in the background — they don't affect the profile card.
        if (ids.customerId) {
          (async () => {
            try {
              const existingResp = await DebtsService.getCustomerSharing(ids.customerId as number, {
                skipErrorCapture: true
              });
              const existingRows = Array.isArray(existingResp?.data) ? existingResp.data : [];
              
              const isNonEmptyString = (v: unknown): v is string =>
                typeof v === 'string' && v.trim().length > 0;
              const isSharedRow = (row: Record<string, unknown>): boolean => {
                const submit = isNonEmptyString(row['submitDate']) ? row['submitDate'].trim() : '';
                const lastShared = isNonEmptyString(row['lastSharedDate'])
                  ? row['lastSharedDate'].trim()
                  : '';
                return Boolean(submit || lastShared);
              };
              const isProfileManagedRow = (row: Record<string, unknown>): boolean => {
                const src = (row['source'] ?? '').toString().trim().toLowerCase();
                const isDeleted = row['isDeleted'] === 'Y';
                return !isDeleted && (src === 'customer_profile' || src === 'referred');
              };
              const toTime = (v: unknown): number => {
                if (!isNonEmptyString(v)) return 0;
                const t = Date.parse(v);
                return Number.isFinite(t) ? t : 0;
              };
              const selectMostRecentUnsharedProfileManaged = (
                rows: unknown[]
              ): Record<string, unknown> | null => {
                const candidates = rows
                  .filter(
                    (r): r is Record<string, unknown> =>
                      typeof r === 'object' && r !== null
                  )
                  .filter((r) => isProfileManagedRow(r) && !isSharedRow(r));
                if (candidates.length === 0) return null;
                return (
                  candidates
                    .slice()
                    .sort((a, b) => {
                      const ta = Math.max(toTime(a['lastUpdateDate']), toTime(a['creationDate']));
                      const tb = Math.max(toTime(b['lastUpdateDate']), toTime(b['creationDate']));
                      return tb - ta;
                    })[0] ?? null
                );
              };

              const existingReferredRecord =
                selectMostRecentUnsharedProfileManaged(existingRows);

              if (shouldUpsertSharing && companyRec) {
                const selectedParentCompany = companyRec.parentCompany || '';
                const rawParent =
                  (companyRec as unknown as { raw?: Record<string, unknown> } | null)?.raw || {};

                const childRawForProduct = (() => {
                  const selectedProductName =
                    typeof (merged1 as any)?.productName === 'string'
                      ? ((merged1 as any).productName as string).trim()
                      : '';
                  if (!selectedProductName) return null;
                  const childrenRaw = (companyRec as unknown as { childrenRaw?: Array<Record<string, unknown>> })
                    .childrenRaw ?? [];
                  const pNorm = normalize(selectedProductName);
                  const match = childrenRaw.find((child) => {
                    const childLender = normalize((child as { lender?: string }).lender);
                    const childDesc = normalize((child as { description?: string }).description);
                    return childLender === pNorm || childDesc === pNorm;
                  });
                  return match || null;
                })();

                const resolveString = (v: unknown): string | undefined => {
                  if (typeof v !== 'string') return undefined;
                  const t = v.trim();
                  return t.length > 0 ? t : undefined;
                };

                const lenderName =
                  resolveString((childRawForProduct as any)?.lender) ??
                  resolveString((rawParent as any)?.lender) ??
                  resolveString(selectedParentCompany) ??
                  '';

                const schemaName =
                  resolveString((childRawForProduct as any)?.schemaName) ??
                  resolveString((rawParent as any)?.schemaName);
                const subDomain =
                  resolveString((childRawForProduct as any)?.subDomain) ??
                  resolveString((rawParent as any)?.subDomain);
                const explicitRef =
                  typeof (merged1 as any)?.referenceId === 'string'
                    ? ((merged1 as any).referenceId as string).trim()
                    : '';
                const accountNumber = explicitRef || ((userEmail as string) || '').trim();

                if (accountNumber) {
                  const sharingPayload: CustomerSharingUpsertRequest = {
                    customerId: Number(ids.customerId),
                    lenderName,
                    accountNumber,
                    ...(schemaName ? { schemaName } : {}),
                    ...(subDomain ? { subDomain } : {}),
                    shareNow: shouldDefaultShareNow(lenderName) ? 'Y' : 'N',
                    isSharing: 'Y',
                    source: 'customer_profile'
                  };

                  const existingId = existingReferredRecord?.['id'];
                  if (existingReferredRecord && typeof existingId === 'number') {
                    await DebtsService.updateCustomerSharing(
                        ({
                          ...(existingReferredRecord as Record<string, unknown>),
                          ...sharingPayload,
                          id: existingId,
                        } as CustomerSharingUpsertRequest & Record<string, unknown>),
                      { skipErrorCapture: true }
                    );
                  } else {
                    await DebtsService.createCustomerSharing(
                      sharingPayload,
                      { skipErrorCapture: true }
                    );
                  }
                }
              } else {
                const existingId = existingReferredRecord?.['id'];
                if (!existingReferredRecord || typeof existingId !== 'number') return;
                const rowSchemaName =
                  typeof existingReferredRecord['schemaName'] === 'string'
                    ? existingReferredRecord['schemaName']
                    : null;
                const rowSubDomain =
                  typeof existingReferredRecord['subDomain'] === 'string'
                    ? existingReferredRecord['subDomain']
                    : null;
                await DebtsService.updateCustomerSharing(
                  ({
                    ...(existingReferredRecord as Record<string, unknown>),
                    id: existingId,
                    customerId: Number(ids.customerId),
                    lenderName:
                      typeof existingReferredRecord['lenderName'] === 'string'
                        ? existingReferredRecord['lenderName']
                        : '',
                    accountNumber:
                      typeof existingReferredRecord['accountNumber'] === 'string'
                        ? existingReferredRecord['accountNumber']
                        : '',
                    schemaName: rowSchemaName,
                    subDomain: rowSubDomain,
                    shareNow: toYN(existingReferredRecord['shareNow']),
                    isSharing: toYN(existingReferredRecord['isSharing']),
                    source: 'unlink_customer_profile'
                  } as CustomerSharingUpsertRequest & Record<string, unknown>),
                  { skipErrorCapture: true }
                );
              }
            } catch { /* non-blocking */ }
          })();
        }
      } catch {
        addAlert({ severity: 'error', message: 'Failed to save referred company', autoHide: true, duration: 2000 });
      } finally {
        setIsSaving(false);
        setLoading('company-save', false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
    else handleDialogClose();
  };

  const currentSchema: ScreenSchema = currentStep === 0 ? (step0Schema as ScreenSchema) : (step1Schema as ScreenSchema);
  const currentInitialData = currentStep === 0 ? (step0Data || {}) : (step1Data || {});
  const currentFormId = currentStep === 0 ? step0FormId : step1FormId;
  const primaryText = currentStep === 0 ? 'Continue' : 'Save';

  // Reset internal state when opening and handle creditor prefill.
  // Use layout effect so prefilled values are applied before the dialog paints
  // (prevents "first open is blank, second open is correct").
  useLayoutEffect(() => {
    if (open) {
      setCurrentStep(0);
      // Reset refs for new dialog session
      hasRestoredRefFromDebtsRef.current = false;
      restoredProductForCompanyKeyRef.current = null;
      hasPrefilledCreditorRef.current = false;
      autoNoReferenceAppliedRef.current = false;
      // Reset debounce guards so queued updates from a previous session don't leak
      queuedStep0UpdateRef.current = null;
      isStep0FlushScheduledRef.current = false;
      queuedStep1UpdateRef.current = null;
      isStep1FlushScheduledRef.current = false;
      // Reset account type option cache so a preselected company will always re-check
      // available account types on each open.
      setAccountTypes([]);
      accountTypesKeyRef.current = null;
      accountTypesLengthRef.current = 0;
      autoSelectedAccountTypeRef.current = null;
      // Normalize incoming prefill to avoid child saved as company
      const norm0 = prefillStep0 || {};
      let norm1 = prefillStep1 || {};
      const rawCompany = normalize((norm1 as any)?.referedFromCompany);
      if (rawCompany) {
        const match = lenders.find((l) => (Array.isArray(l.childCompany) ? l.childCompany : []).some((c) => normalize(c) === rawCompany));
        if (match) {
          norm1 = {
            ...norm1,
            referedFromCompany: typeof match.parentCompany === 'string' ? match.parentCompany.trim() : (norm1 as any)?.referedFromCompany,
            productName: (norm1 as any)?.productName || rawCompany,
            accountType: '',
            referenceId: '',
            noReferenceId: false,
          };
        }
      }
      // Normalize step-0 value (trim). If it's a legacy/unexpected string,
      // `step0Schema` will inject it as an option so it can render selected.
      const norm0Merged: Record<string, unknown> = { ...DEFAULT_STEP0_DATA, ...norm0 };
      if (typeof norm0Merged['referedFrom'] === 'string') {
        norm0Merged['referedFrom'] = (norm0Merged['referedFrom'] as string).trim();
      }
      setStep0Data(norm0Merged);
      setStep1Data({ ...DEFAULT_STEP1_DATA, ...norm1 });
      // Seed last seen referral method for change detection
      try {
        const initialMethod = typeof (norm0 as any)?.referedFrom === 'string' ? (norm0 as any).referedFrom.trim() : '';
        lastReferralMethodRef.current = initialMethod || undefined;
      } catch {}
      // Force Screen to remount so initialData is applied when prefill arrives after mount
      setFormInstanceKey((k) => k + 1);
    } else {
      // Reset refs when dialog closes
      hasRestoredRefFromDebtsRef.current = false;
      restoredProductForCompanyKeyRef.current = null;
      hasPrefilledCreditorRef.current = false;
      autoNoReferenceAppliedRef.current = false;
      // Reset debounce guards so the next dialog open starts clean
      queuedStep0UpdateRef.current = null;
      isStep0FlushScheduledRef.current = false;
      queuedStep1UpdateRef.current = null;
      isStep1FlushScheduledRef.current = false;
    }
  }, [open, prefillStep0, prefillStep1, lenders, userId]);

  return (
    <MobileSlideDialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm" keepMounted={false}>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Edit referred company</span>
        <IconButton
          onClick={handleDialogClose}
          sx={{ color: '#6b7280' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ pt: 1 }}>
          <DialogScreen
            key={`company-referral-${currentStep}-${formInstanceKey}`}
            schema={currentSchema}
            hideHeader
            hideActions
            initialData={currentInitialData}
            onSubmit={(data) => handleSubmitStep(data as Record<string, unknown>)}
            onValuesChange={handleValuesChange}
            formId={currentFormId}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e4e7eb' }}>
        <Button variant="outlined" onClick={handleBack} sx={{ 
          flex: { xs: 1, md: 'initial' },
          minWidth: { xs: 'auto', md: '140px' },
          py: 1.5,
          borderRadius: 2,
          fontSize: '16px',
          fontWeight: 500,
          textTransform: 'none',
          }} disabled={isSaving}>
          {currentStep === 0 ? 'Close' : 'Back'}
        </Button>
        {
          isCreditorReferral && currentStep === 0 && (
            <Button variant="contained" color="primary" sx={{ 
              flex: { xs: 1, md: 'initial' },
              minWidth: { xs: 'auto', md: '140px' },
              py: 1.5,
              borderRadius: 2,
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'none',
              }} onClick={()=> setCurrentStep(1)}>
              Next
            </Button>
          )
        }
        {!isCreditorReferral && (
          <Button variant="contained" color="primary" type="submit" form={currentFormId} sx={{ 
            flex: { xs: 1, md: 'initial' },
            minWidth: { xs: 'auto', md: '140px' },
            py: 1.5,
            borderRadius: 2,
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
            backgroundColor: '#003F44',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#003F44',
              boxShadow: 'none',
              border: '0.5px solid #003F44'
            },
            '&:disabled': {
              backgroundColor: '#E8EFEE',
              color: '#00243E',
            }
            }} disabled={isSaving}>
            {isSaving ? 'Saving...' : primaryText}
          </Button>
        )}
      </DialogActions>
    </MobileSlideDialog>
  );
};

export default CompanyReferralEditDialog;




